import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { colors2, UI_BODY_SM, useBannerContext } from "oolib";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { __CheckAccessToContentType } from "@/utils/accessControl";
import { useSocketContext } from "@/contexts/socketioContext";
import { useBulkDeleteDatasets } from "@/_self_managed/Datasets/useBulkDeleteDatasets";
import { DeleteDatasetConfirmModal } from "@/_self_managed/Datasets/DeleteDatasetConfirmModal";
import { IntegrationModalShell } from "./IntegrationModalShell";
import { DatasetsCard } from "./DatasetsCard";
import { useManagedIntegration } from "./hooks";

/**
 * Manage popup for integrations with nothing to connect or sync (App Store,
 * Play Store): the datasets they import into, each deletable, plus Delete All.
 */
export function ManageDatasetsModal({
  provider,
  name,
  Icon,
  onClose,
  onAddDataset,
}) {
  const m = useManagedIntegration(provider, { name, onClose });
  const userData = useGetQueryData("userData");
  const { importStatusByContentType } = useSocketContext();
  const { SET_INFO_BANNER } = useBannerContext();
  const queryClient = useQueryClient();
  const { mutate: deleteDatasets, isLoading: isDeleting } =
    useBulkDeleteDatasets();
  // Datasets awaiting the confirm popup: one row's, or all for Delete All.
  const [deleteTargets, setDeleteTargets] = useState(null);
  // Same gate as the Datasets module: per-dataset DELETE access, and not
  // while it's importing.
  const isImporting = (d) => importStatusByContentType?.[d.id] === "importingData";
  const datasets = m.datasets.map((d) => ({
    ...d,
    canDelete:
      !isImporting(d) &&
      __CheckAccessToContentType(userData?.user, "DELETE", d.id),
  }));
  const deletable = datasets.filter((d) => d.canDelete);

  const confirmDelete = () => {
    const contentTypes = deleteTargets.map((d) => d.id);
    deleteDatasets(
      { contentTypes },
      {
        onSuccess: ({ success, failed }) => {
          SET_INFO_BANNER(
            failed
              ? `Deleted ${success} of ${contentTypes.length}. ${failed} failed.`
              : `Deleted ${success} dataset${success > 1 ? "s" : ""}.`,
            failed ? "red" : "green",
            5000,
          );
          // The bulk hook leaves this out; the card and this list need it.
          queryClient.invalidateQueries(["externalConnection"]);
          setDeleteTargets(null);
          // Nothing left to manage.
          if (success === datasets.length) onClose();
        },
      },
    );
  };

  return (
    <IntegrationModalShell title={name} Icon={Icon} onClose={onClose}>
      {m.isLoading && (
        <UI_BODY_SM style={{ color: colors2.grey60 }}>Loading…</UI_BODY_SM>
      )}
      {m.errorMsg && (
        <UI_BODY_SM style={{ color: colors2.red }}>{m.errorMsg}</UI_BODY_SM>
      )}
      {!m.isLoading && m.conn && (
        <DatasetsCard
          name={name}
          datasets={datasets}
          canPublish={m.canPublish}
          onAddDataset={onAddDataset}
          onDelete={(d) => setDeleteTargets([d])}
          onDeleteAll={
            deletable.length > 0 ? () => setDeleteTargets(deletable) : undefined
          }
        />
      )}
      {deleteTargets && (
        <DeleteDatasetConfirmModal
          // kp_content_type is what the user types to confirm; title is the
          // modal's subtitle.
          targets={deleteTargets.map((d) => ({
            kp_content_type: d.id,
            general: { content: { title: d.title } },
          }))}
          skipped={
            deleteTargets.length > 1 ? datasets.filter(isImporting).length : 0
          }
          isDeleting={isDeleting}
          // Not dismissable mid-request, so a delete isn't orphaned.
          onClose={() => !isDeleting && setDeleteTargets(null)}
          onConfirm={confirmDelete}
        />
      )}
    </IntegrationModalShell>
  );
}

export default ManageDatasetsModal;
